import {
  UI_PREFS_STORAGE_KEY,
  DEFAULT_METRICS_COLLAPSED,
} from "../types/constants";
import * as logger from "common/logger";
import { useEffect, useRef } from "react";

function withLocalStorageErrorHandling(operation, fn, fallback) {
  try {
    return fn();
  } catch (error) {
    logger.info(operation, error);
    return fallback;
  }
}

export const loadUIPreferences = (reportName) =>
  withLocalStorageErrorHandling(
    "Failed to load UI preferences",
    () => {
      const stored = localStorage.getItem(UI_PREFS_STORAGE_KEY);
      if (!stored) return null;
      const allStates = JSON.parse(stored);
      return allStates[reportName] || null;
    },
    null
  );

export const saveUIPreferences = (reportName, metricsCollapsed) =>
  withLocalStorageErrorHandling(
    "Failed to save UI preferences",
    () => {
      const stored = localStorage.getItem(UI_PREFS_STORAGE_KEY);
      const allStates = stored ? JSON.parse(stored) : {};
      const existingState = allStates[reportName] || {};
      allStates[reportName] = {
        metricsCollapsed:
          metricsCollapsed ?? existingState.metricsCollapsed ?? undefined,
      };
      localStorage.setItem(UI_PREFS_STORAGE_KEY, JSON.stringify(allStates));
    },
    undefined
  );

export const useLocalStorageSync = ({
  reportName,
  metricsCollapsed,
  setMetricsCollapsed,
}) => {
  const hasLoadedUIPrefs = useRef(false);
  const reportNameRef = useRef(reportName);
  reportNameRef.current = reportName;

  useEffect(() => {
    saveUIPreferences(reportNameRef.current, metricsCollapsed);
  }, [metricsCollapsed]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const uiPrefs = loadUIPreferences(reportName);

    if (hasLoadedUIPrefs.current) {
      setMetricsCollapsed(
        uiPrefs?.metricsCollapsed ?? DEFAULT_METRICS_COLLAPSED
      );
    }

    hasLoadedUIPrefs.current = true;
  }, [reportName, setMetricsCollapsed]);
};
